import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Grid,
  Box,
  Paper,
  Avatar,
  CircularProgress,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { PhotoCamera, Save, Delete, Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import CustomTextField from "../components/RTLTextField";
import { EditUserFormData } from "../redux/slices/user/thunk";
import { useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";

const UserProfile = () => {
  
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

  // Get user from localStorage
  const userFromStorage = JSON.parse(localStorage.getItem('user'));
  const [user, setUser] = useState(userFromStorage || {});

  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);

  // Validation schema - location required
  const validationSchema = Yup.object().shape({
    userName: Yup.string().required(t("userForm.validation.nameRequired")),
    phoneNumber: Yup.string()
      .required(t("userForm.validation.phoneRequired")),
      // .matches(/^[0-9]{10}$/, t("userForm.validation.phoneInvalid")),
    email: Yup.string()
      .email(t("validation.invalidEmail"))
      .required(t("validation.emailRequired")),
    address: Yup.string().required(t("userForm.validation.addressRequired")),
    location: Yup.string()
      .url(t("userForm.validation.locationLinkInvalid")),
      // .required(t("userForm.validation.locationLinkRequired")),
    password: Yup.string()
      .min(6, t("userForm.validation.passwordMin"))
      .optional(),
  });

  // Formik initialization with real user data
  const formik = useFormik({
    initialValues: {
      userName: user?.userName || "",
      phoneNumber: user?.phoneNumber || "",
      email: user?.email || "",
      address: user?.address || "",
      location: user?.location || "",
      image: user?.image || "",
      password: "",
    },
    validationSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true,
  });

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      formik.setValues({
        userName: user.userName || "",
        phoneNumber: user.phoneNumber || "",
        email: user.email || "",
        address: user.address || "",
        location: user.location || "",
        image: user.image || "",
        password: "",
      });
    }
  }, [user]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        formik.setFieldValue("image", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    formik.setFieldValue("image", "");
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    // Prepare form data
    const formData = new FormData();
    
    // Append all fields except password if empty
    Object.entries(values).forEach(([key, value]) => {
      if (key === "password") {
        if (value && value.trim() !== "") {
          formData.append(key, value);
        }
      } else if (key === "image" && photoFile) {
        formData.append("image", photoFile);
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    const id = user?._id;
    const response = await dispatch(EditUserFormData({ id, formData }));
    
    if (response.payload && response.payload.data) {
      // Update localStorage with new user data
      const updatedUser = { ...user, ...response.payload.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      // Reset password field
      formik.setFieldValue("password", "");
    }
    
    setLoading(false);
  };
  if (user?.type !== "client") {
    return <Navigate to="/" replace />;
  }
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box mb={3} textAlign="center">
          <Typography variant="h4" component="h1" gutterBottom>
            {t("userProfile.title")}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            {t("userProfile.subtitle")}
          </Typography>
        </Box>

        {/* Photo Upload Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4,
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            p: 3,
            position: "relative",
          }}
        >
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="photo-upload"
            type="file"
            onChange={handlePhotoChange}
          />

          {formik.values.image ? (
            <>
              <Avatar
                src={formik.values.image}
                sx={{
                  width: 120,
                  height: 120,
                  border: `3px solid ${theme.palette.primary.main}`,
                  mb: 2,
                }}
              />
              <Box sx={{ display: "flex", gap: 1 }}>
                <label htmlFor="photo-upload">
                  <Button
                    variant="outlined"
                    color="primary"
                    component="span"
                    startIcon={<PhotoCamera sx={{ ml: 2 }} />}
                  >
                    {t("userForm.photo.change")}
                  </Button>
                </label>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete sx={{ ml: 2 }} />}
                  onClick={handleRemovePhoto}
                >
                  {t("userForm.photo.remove")}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  bgcolor: theme.palette.grey[200],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <PhotoCamera
                  sx={{ fontSize: 40, color: theme.palette.grey[500] }}
                />
              </Box>
              <Typography variant="body1" color="textSecondary" mb={2}>
                {t("userForm.photo.optional")}
              </Typography>
              <label htmlFor="photo-upload">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<PhotoCamera sx={{ ml: 2 }} />}
                >
                  {t("userForm.photo.upload")}
                </Button>
              </label>
            </>
          )}
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Mandatory Fields */}
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.fullName")}
                name="userName"
                value={formik.values.userName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.userName && Boolean(formik.errors.userName)}
                helperText={formik.touched.userName && formik.errors.userName}
                isRtl={isArabic}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.phoneNumber")}
                name="phoneNumber"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                required
                isRtl={isArabic}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {user?.countryCode || "+966"}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.email")}
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
                isRtl={isArabic}
              />
            </Grid>

            {/* Password Field */}
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.password")}
                name="password"
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isRtl={isArabic}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={
                  formik.touched.password && formik.errors.password
                    ? formik.errors.password
                    : t("userForm.messages.leavePasswordBlank")
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.address")}
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                isRtl={isArabic}
                required
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label={t("userForm.fields.locationLink")}
                name="location"
                value={formik.values.location}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isRtl={isArabic}
                error={
                  formik.touched.location &&
                  Boolean(formik.errors.location)
                }
                helperText={
                  formik.touched.location && formik.errors.location
                }
                // required
                placeholder="https://maps.app.goo.gl/..."
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <Save sx={{ ml: 2 }} />
                    )
                  }
                  disabled={loading}
                  sx={{ py: 1.5 }}
                >
                  {t("userProfile.buttons.saveChanges")}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default UserProfile;